import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permission } from '@hp/domain';
import { trpc, type RouterOutputs } from './trpc';

/** The signed-in user as the API describes it; null while anonymous (a valid state on the landing page). */
export type Me = NonNullable<RouterOutputs['auth']['me']>;

export function useMe() {
  return useQuery(trpc.auth.me.queryOptions(undefined, { staleTime: 60_000 }));
}

/**
 * `can('crm.merge')` — the UI-side twin of the API's permission guard, so buttons the
 * API would refuse are hidden or disabled. False while the session is still loading.
 */
export function useCan(): (permission: Permission) => boolean {
  const me = useMe();
  const permissions = me.data?.permissions;
  return (permission) => permissions?.includes(permission) ?? false;
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    // Logout clears the session cookie — a browser round trip, so it stays REST.
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    await qc.invalidateQueries(trpc.auth.me.pathFilter());
    window.location.href = '/';
  };
}
